/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import BaseVimeoPlayer from '@vimeo/player';

import {Player} from '@amblique/base/app/plugins/video/adapters/player';
import {VimeoPlayer} from '@amblique/base/app/plugins/video/adapters/vimeo-player';
import {AbstractVideo} from '@amblique/base/app/plugins/video/providers/abstract-video';

/**
 * Vimeo model
 */
export class Vimeo extends AbstractVideo {
  static TYPE = 'vimeo';
  static PLAYER = VimeoPlayer;
  static PARAMS = ['id', 'url', 'autopause', 'autoplay', 'background', 'byline', 'color', 'controls', 'height', 'loop', 'maxheight', 'maxwidth', 'muted', 'playsinline', 'portrait', 'speed', 'title', 'transparent', 'width'];
  static EVENTS = ['play', 'playing', 'pause', 'ended', 'timeupdate', 'progress', 'seeking', 'seeked', 'texttrackchange', 'chapterchange', 'cuechange', 'cuepoint', 'volumechange', 'playbackratechange', 'bufferstart', 'bufferend', 'error', 'loaded', 'durationchange', 'fullscreenchange', 'qualitychange', 'camerachange', 'resize'];
  static ID_PATTERN = /(?:^|\/|=|:)(\d+(?:\/\w+)?)(?:\?|&|\/|"|,|$)/;
  static TYPE_PATTERN = /vimeo|^\d+(?:\/\w+)?(?:\?|,|$)/;

  /**
   * @protected
   */
  get oembedUrl() {
    const videoUrl = `https://vimeo.com/${this.id}`;
    const oembedUrl = 'https://vimeo.com/api/oembed.json';
    const params = new URLSearchParams(Object.entries({...this.options.params, url: videoUrl}));

    return `${oembedUrl}?${params.toString()}`;
  }

  /**
   * Create new Vimeo player instance for the given response metadata
   *
   * @protected
   *
   * @param {object} metadata Vimeo metadata object, see [Vimeo: Working with oEmbed](https://developer.vimeo.com/api/oembed/videos#embedding-a-video-with-oembed-step-1)
   *
   * @returns {Player} Returns new Vimeo player instance
   */
  createPlayer(metadata) {
    const div = document.createElement('div');

    div.innerHTML = metadata.metadata.html.replace(' allowfullscreen ', ' ');
    this.applyClip(div.firstChild);
    this.element.appendChild(div.firstChild);

    // Create provider-specific player adapter
    const vimeoPlayerAdapter = new VimeoPlayer(
      new BaseVimeoPlayer(this.element, this.options.params)
    );

    // Wrap in unified Player
    this.player = new Player(vimeoPlayerAdapter);

    return this.player;
  }

  /**
   * Register video events configured in options
   *
   * @protected
   *
   * @param {Player} player The Vimeo player instance
   *
   * @returns {Player} Returns Vimeo player instance
   */
  registerEvents(player) {
    super.registerEvents(player);

    if (typeof this.options.onReady === 'function') {
      // Access the native Vimeo player through the adapter
      player.adapter.player.ready().then((...args) => this.options.onReady(this, ...args));
    }

    return player;
  }
}
